import { useCallback, useMemo } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useAuth } from '@/features/auth/auth-context'
import { acceptInvitation } from '@/features/settings/invitations-api'
import { DropdownMenu } from '@/shared/components/ui/dropdown-menu'
import {
  type RealtimeChannel,
  useRealtimeSubscriptions,
} from '@/shared/realtime/use-realtime-subscriptions'
import { cn } from '@/shared/utils/cn'
import { formatDate } from '@/shared/utils/date'

import { notificationTarget } from './notification-target'
import {
  type AppNotification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from './notifications-api'

const notificationsQueryKey = ['notifications'] as const

export function NotificationBell() {
  const { user, isAuthenticated, refreshUser } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const listQuery = useQuery({
    queryKey: notificationsQueryKey,
    queryFn: listNotifications,
    enabled: isAuthenticated,
    retry: false,
  })

  const channels = useMemo<RealtimeChannel[]>(
    () => (user ? [{ name: `App.Models.User.${user.id}`, private: true }] : []),
    [user],
  )

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: notificationsQueryKey })
  }, [queryClient])

  useRealtimeSubscriptions({ channels, enabled: isAuthenticated, onEvent: invalidate })

  const markReadMutation = useMutation({ mutationFn: markNotificationRead, onSuccess: invalidate })
  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidate,
  })
  const acceptInviteMutation = useMutation({
    mutationFn: acceptInvitation,
    onSuccess: async () => {
      await refreshUser()
      // Активная организация сменилась — сбрасываем кэш прошлого контекста.
      queryClient.clear()
      toast.success('Вы присоединились к организации')
      navigate('/projects')
    },
  })

  const notifications = listQuery.data || []
  // Индикатор-точка выводится из загруженного списка — отдельный запрос
  // unread-count не нужен (для точки достаточно факта наличия непрочитанных).
  const unreadCount = notifications.filter((notification) => !notification.read_at).length

  const onOpenNotification = (notification: AppNotification, close: () => void) => {
    if (!notification.read_at) {
      markReadMutation.mutate(notification.id)
    }

    const target = notificationTarget(notification.data)
    close()

    if (target) {
      navigate(target)
    }
  }

  return (
    <DropdownMenu
      trigger={
        <span className="relative flex h-8 w-8 items-center justify-center rounded-lg text-[var(--color-text-soft)] hover:bg-[var(--panel-subtle)]">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 ? (
            <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[var(--color-accent)]" />
          ) : null}
        </span>
      }
    >
      {(close) => (
        <div className="w-[320px]">
          <div className="flex items-center justify-between border-b border-[var(--panel-border)] px-2 py-2">
            <p className="text-sm font-semibold">Уведомления</p>
            {unreadCount > 0 ? (
              <button
                className="text-xs text-[var(--color-accent)] hover:underline"
                onClick={() => markAllMutation.mutate()}
                type="button"
              >
                Прочитать все
              </button>
            ) : null}
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="px-2 py-4 text-center text-sm text-[var(--color-text-muted)]">
                Уведомлений пока нет.
              </p>
            ) : (
              notifications.map((notification) => {
                const isInvite =
                  notification.data.type === 'organization.invitation_received' &&
                  Boolean(notification.data.token)

                return (
                  <div
                    className={cn(
                      'rounded-sm px-2 py-2 transition hover:bg-[var(--panel-subtle)]',
                      !notification.read_at && 'bg-[var(--color-accent)]/8',
                    )}
                    key={notification.id}
                  >
                    <button
                      className="block w-full text-left text-sm"
                      onClick={() => onOpenNotification(notification, close)}
                      type="button"
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span className="font-medium text-[var(--color-text)]">
                          {notification.data.title}
                        </span>
                        {!notification.read_at ? (
                          <span className="mt-1 h-2 w-2 flex-none rounded-full bg-[var(--color-accent)]" />
                        ) : null}
                      </span>
                      {notification.data.body ? (
                        <span className="mt-0.5 line-clamp-2 block text-xs text-[var(--color-text-soft)]">
                          {notification.data.body}
                        </span>
                      ) : null}
                      <span className="mt-0.5 block text-xs text-[var(--color-text-muted)]">
                        {formatDate(notification.created_at)}
                      </span>
                    </button>
                    {isInvite ? (
                      <button
                        className="mt-1.5 rounded-md bg-[var(--color-accent)] px-2.5 py-1 text-xs font-medium text-white disabled:opacity-60"
                        disabled={acceptInviteMutation.isPending}
                        onClick={() => {
                          if (!notification.read_at) markReadMutation.mutate(notification.id)
                          acceptInviteMutation.mutate(notification.data.token as string)
                          close()
                        }}
                        type="button"
                      >
                        Принять
                      </button>
                    ) : null}
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </DropdownMenu>
  )
}
